import { useEffect, useState, memo } from "react";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined"; // bid
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined"; // project
import {
    Chip,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from "@mui/material";
import { CalendarMark } from "@solar-icons/react/ssr"; // milestone
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import AiLogo from "@/ai/AiLogo";
import { request } from "@/Api";
import { selectPersonId } from "@/auth/authSlice";
import { PERMISSION_GET_PROJECTS } from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import { Comment } from "@/common/Comments";
import { DottedSum, PageTitle } from "@/common/CommonComponents";
import { Heading, SectionTitle } from "@/common/typography";
import { FEATURE_AI_INTAKE, FEATURE_BID_APPROVAL_FLOW } from "@/constants";
import { openProjectWorkshop } from "@/nav/navSlice";
import Projects from "@/projects/Projects";

import { formatDateStringToDayMonth } from "@/utils";

const Overview = memo(({ person, hasBidApprovalFlow }) => {
    const dispatch = useDispatch();
    const { hasFeature } = useEntitlements();
    const [hovering, setHovering] = useState(false);
    const handleHover = () => {
        setHovering(true);
    };
    const handleLeave = () => {
        setHovering(false);
    };
    const handleProjectWorkshopOpen = () => {
        dispatch(
            openProjectWorkshop({
                open: true,
                activeAgent: "intake",
            }),
        );
    };
    const background =
        "linear-gradient(135deg, rgba(66, 133, 244, 0.7) 15.58%, rgba(39, 199, 126, 0.7) 46.72%, rgba(255, 172, 48, 0.7) 71.58%, rgba(227, 67, 54, 0.7) 85.58%)";

    return (
        <>
            <SectionTitle>Overview</SectionTitle>
            <Grid container spacing={2} alignItems="stretch">
                {hasFeature(FEATURE_AI_INTAKE) && (
                    <Grid
                        item
                        onClick={handleProjectWorkshopOpen}
                        flexGrow={1}
                        xs={12}
                        sm={6}
                        md={3}
                        sx={{ cursor: "pointer" }}
                        onMouseEnter={handleHover}
                        onMouseLeave={handleLeave}
                    >
                        <div
                            style={{
                                borderRadius: 10,
                                height: "100%",
                                background: background,
                                backgroundSize: hovering ? "200%" : "100%",
                                transition: "all 0.5s ease",
                            }}
                        >
                            <div style={{ padding: "16px" }}>
                                <Grid
                                    container
                                    justifyContent={"space-between"}
                                    alignItems={"center"}
                                >
                                    <Grid item>
                                        <AiLogo
                                            additionalStyles={{
                                                width: 24,
                                                height: 24,
                                                filter: "brightness(0) invert(100%)",
                                                display: "block",
                                            }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Chip
                                            size="small"
                                            label={
                                                <span
                                                    style={{
                                                        paddingLeft: 2,
                                                        paddingRight: 2,
                                                        background:
                                                            "linear-gradient(135deg, #4285F4 15.58%, #27C77E 46.72%, #FFAC30 71.58%, #E34336 85.58%)",
                                                        WebkitBackgroundClip:
                                                            "text",
                                                        WebkitTextFillColor:
                                                            "transparent",
                                                    }}
                                                >
                                                    Beta
                                                </span>
                                            }
                                            style={{
                                                background: "white",
                                            }}
                                            variant="filled"
                                            color="default"
                                        />
                                    </Grid>
                                </Grid>
                                <p
                                    style={{
                                        margin: "30px 0 6px 0",
                                        fontWeight: 600,
                                        fontSize: 18,
                                    }}
                                >
                                    Create with AI in minutes
                                </p>
                                <p
                                    style={{
                                        fontSize: 10,
                                        lineHeight: "12px",
                                        margin: 0,
                                    }}
                                >
                                    Get a brief, project and bid to get you
                                    started
                                </p>
                            </div>
                        </div>
                    </Grid>
                )}

                <Grid item flexGrow={1} xs={12} sm={6} md={3}>
                    <DottedSum
                        icon={WorkOutlineOutlinedIcon}
                        color="#4285F4"
                        title="Projects"
                        subTitle="Briefed"
                        amount={person.projects_count_briefed}
                    />
                </Grid>
                <Grid item flexGrow={1} xs={12} sm={6} md={3}>
                    <DottedSum
                        icon={WorkOutlineOutlinedIcon}
                        color="#27C77E"
                        title="Projects"
                        subTitle="In Production"
                        amount={person.projects_count_in_production}
                    />
                </Grid>
                {hasBidApprovalFlow && (
                    <Grid item flexGrow={1} xs={12} sm={6} md={3}>
                        <DottedSum
                            icon={ArticleOutlinedIcon}
                            color="#FFAC30"
                            title="Bid Sheets"
                            subTitle="Pending"
                            amount={person.bids_count}
                        />
                    </Grid>
                )}
            </Grid>
        </>
    );
});

Overview.displayName = "Overview";

const DashboardMilestones = memo(({ milestones }) => {
    return (
        <>
            <SectionTitle>Upcoming Milestones</SectionTitle>
            <List disablePadding>
                {milestones.map((milestone, i) => (
                    <div key={i}>
                        <ListItem
                            secondaryAction={
                                <Chip
                                    variant="outlined"
                                    label={formatDateStringToDayMonth(
                                        milestone.complete_date,
                                    )}
                                />
                            }
                            disablePadding
                        >
                            <ListItemButton
                                href={`/projects/${milestone.project_id}?milestone=${milestone.id}`}
                                sx={{ paddingLeft: 0 }}
                            >
                                <ListItemIcon>
                                    <CalendarMark />
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{ noWrap: true }}
                                    sx={{ marginRight: 6 }}
                                >
                                    {milestone.name}
                                </ListItemText>
                            </ListItemButton>
                        </ListItem>
                        <Divider sx={{ borderColor: "#303030" }} />
                    </div>
                ))}
            </List>
        </>
    );
});

DashboardMilestones.displayName = "DashboardMilestones";

const DashboardComments = memo(({ comments }) => {
    return (
        <div>
            <Typography variant="h2">Unresolved Comments</Typography>
            <div style={{ maxHeight: "70vh", overflow: "auto" }}>
                {comments.map((comment, index) => (
                    <Comment key={index} comment={comment} showProjectLink />
                ))}
            </div>
        </div>
    );
});

DashboardComments.displayName = "DashboardComments";

const DashboardProjects = memo(({ personId }) => {
    return (
        <div>
            <SectionTitle>Projects</SectionTitle>
            <Projects
                prefixedFilters={{
                    person_id: personId,
                }}
                prefixedTableViewFields={[
                    "docket",
                    "name",
                    "status_id",
                    "start_date",
                    "end_date",
                    "bid_quoted",
                ]}
            />
        </div>
    );
});

DashboardProjects.displayName = "DashboardProjects";

export default function Dashboard() {
    const navigate = useNavigate();
    const { state } = useLocation(); // used to trigger reload when switching organization
    const [person, setPerson] = useState(null);

    const personId = useSelector(selectPersonId);
    const { hasPermission, hasFeature } = useEntitlements();
    const canViewProjects = hasPermission(PERMISSION_GET_PROJECTS);
    const hasBidApprovalFlow = hasFeature(FEATURE_BID_APPROVAL_FLOW);

    const getDashboard = () => {
        request.get("dashboard").then((response) => {
            setPerson(response.data);
        });
    };

    useEffect(() => {
        if (!canViewProjects) {
            navigate("/profile");
        }
        setPerson(null); // needed to trigger re-render of projects section
        getDashboard();
    }, [state]);

    if (!person) {
        return;
    }

    return (
        <>
            <Helmet>
                <PageTitle title="Dashboard" />
            </Helmet>
            <Heading
                style={{ marginBottom: 40, marginTop: 13 }}
            >{`Welcome, ${person.first_name}.`}</Heading>

            <Grid container spacing={6} paddingBottom={6}>
                <Grid item md={8.5} sm={12}>
                    <Overview
                        person={person}
                        hasBidApprovalFlow={hasBidApprovalFlow}
                    />
                </Grid>
                <Grid item md={3.5} sm={12} flexGrow={1}>
                    <DashboardMilestones milestones={person.milestones} />
                </Grid>
            </Grid>

            <Grid container spacing={6} paddingBottom={6}>
                <Grid
                    item
                    md={person.comments_unresolved.length > 0 ? 8.5 : 12}
                    width={"100%"}
                >
                    <DashboardProjects personId={`eq:${personId}`} />
                </Grid>
                {person.comments_unresolved.length > 0 ? (
                    <Grid item md={3.5}>
                        <DashboardComments
                            comments={person.comments_unresolved}
                        />
                    </Grid>
                ) : null}
            </Grid>
        </>
    );
}
